import { Component, Input, OnInit } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { HeaderService } from '@core/services/header.service';
import { PermissionService } from '@core/services/permission.service';

@Component({
  selector: 'app-shifts',
  standalone: false,
  templateUrl: './shifts.html',
  styleUrl: './shifts.scss',
})
export class Shifts implements OnInit {

  @Input() embedTab = '';

  activeTab = 'list';
  shifts: any[] = [];
  employees: any[] = [];
  empNameFn    = (e: any) => e ? `${e.firstName || ''} ${e.lastName || ''}`.trim() : '';
  shiftLabelFn = (s: any) => s ? `${s.name} (${this.formatTime12(s.startTime)} – ${this.formatTime12(s.endTime)})` : '';
  teamLabelFn  = (t: any) => t ? `${t.name} (${(this.teamMembersMap[t.id] || []).length} members)` : '';

  teamStatusOptions = [
    { label: 'Active',   value: 'active'   },
    { label: 'Inactive', value: 'inactive' }
  ];
  overrideOptions = [
    { label: 'Yes — apply to all members',          value: 'yes' },
    { label: 'No — skip members with custom shifts', value: 'no'  }
  ];
  workingDaysOptions = [
    { label: 'Mon–Fri', value: 'Mon–Fri' },
    { label: 'Mon–Sat', value: 'Mon–Sat' }
  ];

  readonly hours   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  readonly minutes = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

  get startHour()       { return this.shiftForm.startTime?.split(':')[0] || '09'; }
  set startHour(v: string) { this.shiftForm.startTime = `${v}:${this.startMin}`; }
  get startMin()        { return this.shiftForm.startTime?.split(':')[1] || '00'; }
  set startMin(v: string)  { this.shiftForm.startTime = `${this.startHour}:${v}`; }
  get endHour()         { return this.shiftForm.endTime?.split(':')[0] || '18'; }
  set endHour(v: string)   { this.shiftForm.endTime = `${v}:${this.endMin}`; }
  get endMin()          { return this.shiftForm.endTime?.split(':')[1] || '00'; }
  set endMin(v: string)    { this.shiftForm.endTime = `${this.endHour}:${v}`; }
  assignments: any[] = [];
  teams: any[] = [];
  departments: any[] = [];
  loading = true;
  showAddShift = false;
  submitting = false;
  selectedTeam: any = null;
  showAddMember = false;
  addMemberEmployeeId = '';
  showEditTeam = false;
  showCreateTeam = false;
  assignMode: 'team' | 'employee' = 'team';
  editTeamForm: any = {};
  teamMembersMap: { [teamId: string]: any[] } = {};
  pendingMembers: any[] = [];
  memberSearch = '';

  shiftForm: any = { name: '', startTime: '09:00', endTime: '18:00', graceMinutes: 15, workingDays: 'Mon–Fri', breakMinutes: 60 };

  teamForm: any = { name: '', departmentId: '', teamLeadId: '', defaultShiftId: '', description: '' };

  teamShiftForm: any = { teamId: '', shiftId: '', effectiveFrom: '', effectiveUntil: '', override: 'yes', notes: '' };

  empShiftForm: any = { employeeId: '', shiftId: '', effectiveFrom: '', effectiveUntil: '', reason: '' };


  constructor(
    private apiService: ApiService,
    private headerService: HeaderService,
    public permissions: PermissionService
  ) {}

  ngOnInit(): void {
    if (this.embedTab) this.activeTab = this.embedTab;
    else this.headerService.setTitle('Teams & Shifts');
    this.loadData();
    this.loadDepartments();
  }

  switchTab(tab: string): void {
    if (tab === 'shift-team') { this.activeTab = 'assign'; this.assignMode = 'team'; return; }
    if (tab === 'shift-emp')  { this.activeTab = 'assign'; this.assignMode = 'employee'; return; }
    this.activeTab = tab;
  }

  private extractList(data: any): any[] {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  }

  loadData(): void {
    this.loading = true;
    this.apiService.get('/shifts').subscribe({
      next: (data: any) => {
        this.shifts = this.extractList(data);
        if (this.shifts.length === 0) this.shifts = this.getDefaultShifts();
        this.loadEmployees();
      },
      error: () => {
        this.shifts = this.getDefaultShifts();
        this.loadEmployees();
      }
    });
  }

  private loadEmployees(): void {
    this.apiService.get('/employees').subscribe({
      next: (data: any) => {
        this.employees = this.extractList(data);
        this.loadTeams();
        this.loadAssignments();
      },
      error: () => { this.loading = false; }
    });
  }

  private loadAssignments(): void {
    this.apiService.get('/roster').subscribe({
      next: (data: any) => {
        const list = this.extractList(data);
        this.assignments = list.map((a: any) => {
          const emp = this.employees.find(e => e.id === a.employeeId);
          const shift = this.shifts.find(s => s.id === a.shiftId);
          return { ...a, employeeName: emp ? (emp.firstName + ' ' + emp.lastName) : 'Unknown', shiftName: shift?.name || 'Unknown' };
        });
        this.loading = false;
      },
      error: () => { this.assignments = []; this.loading = false; }
    });
  }

  getShiftBadgeColor(index: number): string {
    const bgs = ['rgba(139,26,26,0.1)', 'rgba(30,64,175,0.1)', 'rgba(180,83,9,0.1)', 'rgba(22,101,52,0.1)', '#f3e8ff'];
    return bgs[index % bgs.length];
  }
  getShiftTextColor(index: number): string {
    const txs = ['#8b1a1a', '#1e40af', '#b45309', '#166534', '#6b21a8'];
    return txs[index % txs.length];
  }

  formatTime12(time: string): string {
    if (!time) return '';
    const parts = time.split(':');
    let h = +parts[0];
    const m = parts[1];
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${h}:${m} ${ampm}`;
  }

  addShift(): void {
    if (!this.shiftForm.name || !this.shiftForm.startTime || !this.shiftForm.endTime) return;
    this.submitting = true;
    this.apiService.post('/shifts', this.shiftForm).subscribe({
      next: (s: any) => { this.shifts.push(s); this.resetShiftForm(); this.submitting = false; },
      error: () => {
        this.shifts.push({ ...this.shiftForm, id: Date.now() });
        this.resetShiftForm(); this.submitting = false;
      }
    });
  }

  loadTeams(): void {
    this.apiService.get('/teams').subscribe({
      next: (data: any) => {
        this.teams = this.extractList(data);
        this.teams.forEach((t: any) => this.loadTeamMembers(t.id));
        if (this.teams.length > 0 && !this.selectedTeam) {
          this.selectedTeam = this.teams[0];
        }
      },
      error: () => { this.teams = []; }
    });
  }

  loadTeamMembers(teamId: string): void {
    this.apiService.get('/teams/' + teamId + '/members').subscribe({
      next: (data: any) => { this.teamMembersMap[teamId] = this.extractList(data); },
      error: () => { this.teamMembersMap[teamId] = []; }
    });
  }

  selectTeam(team: any): void {
    this.selectedTeam = this.selectedTeam?.id === team.id ? null : team;
  }

  addTeamMember(): void {
    if (!this.selectedTeam || !this.addMemberEmployeeId) return;
    this.apiService.post('/teams/' + this.selectedTeam.id + '/members', { employeeId: this.addMemberEmployeeId }).subscribe({
      next: () => {
        this.loadTeamMembers(this.selectedTeam.id);
        this.addMemberEmployeeId = '';
        this.showAddMember = false;
      },
      error: () => {}
    });
  }

  removeTeamMember(teamId: string, employeeId: string): void {
    this.apiService.delete('/teams/' + teamId + '/members/' + employeeId).subscribe({
      next: () => { this.loadTeamMembers(teamId); },
      error: () => { this.loadTeamMembers(teamId); }
    });
  }

  loadDepartments(): void {
    this.apiService.get('/departments').subscribe({
      next: (data: any) => { this.departments = this.extractList(data); },
      error: () => { this.departments = []; }
    });
  }

  createTeam(): void {
    if (!this.teamForm.name) return;
    this.submitting = true;
    this.apiService.post('/teams', this.teamForm).subscribe({
      next: (team: any) => {
        this.teams.push(team);
        if (this.pendingMembers.length > 0) {
          this.pendingMembers.forEach(m => {
            this.apiService.post('/teams/' + team.id + '/members', { employeeId: m.id }).subscribe({ next: () => {}, error: () => {} });
          });
          setTimeout(() => this.loadTeamMembers(team.id), 500);
        }
        this.teamForm = { name: '', departmentId: '', teamLeadId: '', defaultShiftId: '', description: '' };
        this.pendingMembers = [];
        this.memberSearch = '';
        this.submitting = false;
        this.showCreateTeam = false;
        this.activeTab = 'list';
      },
      error: () => { this.submitting = false; }
    });
  }

  openEditTeam(team: any): void {
    this.editTeamForm = {
      id: team.id,
      name: team.name,
      departmentId: team.departmentId || '',
      teamLeadId: team.teamLeadId || '',
      defaultShiftId: team.defaultShiftId || '',
      description: team.description || '',
      status: team.status || 'active'
    };
    this.showEditTeam = true;
  }

  saveEditTeam(): void {
    if (!this.editTeamForm.name) return;
    this.submitting = true;
    const { id, ...payload } = this.editTeamForm;
    this.apiService.patch('/teams/' + id, payload).subscribe({
      next: () => {
        const idx = this.teams.findIndex(t => t.id === id);
        if (idx !== -1) this.teams[idx] = { ...this.teams[idx], ...payload };
        if (this.selectedTeam?.id === id) this.selectedTeam = { ...this.selectedTeam, ...payload };
        this.showEditTeam = false;
        this.submitting = false;
      },
      error: () => { this.submitting = false; }
    });
  }

  deleteTeam(id: string): void {
    this.apiService.delete('/teams/' + id).subscribe({
      next: () => { this.teams = this.teams.filter(t => t.id !== id); this.selectedTeam = null; },
      error: () => { this.teams = this.teams.filter(t => t.id !== id); this.selectedTeam = null; }
    });
  }

  getDeptName(deptId: string): string {
    const dept = this.departments.find((d: any) => d.id === deptId);
    return dept ? dept.name : '-';
  }

  getLeadName(leadId: string): string {
    const emp = this.employees.find((e: any) => e.id === leadId);
    return emp ? (emp.firstName + ' ' + emp.lastName) : '-';
  }

  getEmployee(employeeId: string): any {
    return this.employees.find((e: any) => e.id === employeeId) || null;
  }

  getShiftName(shiftId: string): string {
    const shift = this.shifts.find((s: any) => s.id === shiftId);
    return shift ? shift.name : '-';
  }

  assignShiftToTeam(): void {
    if (!this.teamShiftForm.teamId || !this.teamShiftForm.shiftId) return;
    this.submitting = true;
    this.apiService.patch('/teams/' + this.teamShiftForm.teamId, { defaultShiftId: this.teamShiftForm.shiftId }).subscribe({
      next: () => {
        this.loadTeams();
        this.teamShiftForm = { teamId: '', shiftId: '', effectiveFrom: '', effectiveUntil: '', override: 'yes', notes: '' };
        this.submitting = false;
      },
      error: () => { this.submitting = false; }
    });
  }

  assignShiftToEmployee(): void {
  if (!this.empShiftForm.employeeId || !this.empShiftForm.shiftId) return;

  this.submitting = true;

  this.apiService.post('/roster', {
    employeeId: this.empShiftForm.employeeId,
    shiftId: this.empShiftForm.shiftId,
    date: this.empShiftForm.effectiveFrom,
    notes: this.empShiftForm.reason
  }).subscribe({
    next: () => {
      this.loadData();

      this.empShiftForm = {
        employeeId: '',
        shiftId: '',
        effectiveFrom: '',
        effectiveUntil: '',
        reason: ''
      };

      this.submitting = false;
    },
    error: () => {
      this.submitting = false;
    }
  });
}

  deleteShift(id: any): void {
    this.apiService.delete('/shifts/' + id).subscribe({
      next: () => { this.shifts = this.shifts.filter(s => s.id !== id); },
      error: () => { this.shifts = this.shifts.filter(s => s.id !== id); }
    });
  }

  resetShiftForm(): void {
    this.shiftForm = { name: '', startTime: '09:00', endTime: '18:00', graceMinutes: 15, workingDays: 'Mon–Fri', breakMinutes: 60 };
    this.showAddShift = false;
  }

  getEmpInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  }

  getAvatarColor(name: string): string {
    const colors = ['#334155', '#475569', '#0f766e', '#166534', '#92400e', '#7c3a5a', '#3f4a5f', '#5b6472'];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  getAvatarGradient(name: string): string {
    const gradients = [
      'linear-gradient(135deg, #3f4f66 0%, #334155 100%)',
      'linear-gradient(135deg, #55617a 0%, #475569 100%)',
      'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
      'linear-gradient(135deg, #166534 0%, #16a34a 100%)',
      'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
      'linear-gradient(135deg, #7c3a5a 0%, #9d5a7a 100%)',
    ];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return gradients[Math.abs(hash) % gradients.length];
  }

  get filteredEmployeesForAdd(): any[] {
    const term = this.memberSearch.toLowerCase();
    const pendingIds = this.pendingMembers.map(m => m.id);
    let list = this.employees.filter(e => !pendingIds.includes(e.id));
    if (term) {
      list = list.filter(e =>
        (e.firstName + ' ' + e.lastName).toLowerCase().includes(term) ||
        (e.employeeId || '').toLowerCase().includes(term)
      );
    }
    return list;
  }

  addPendingMember(emp: any): void {
    if (!this.pendingMembers.find(m => m.id === emp.id)) {
      this.pendingMembers.push(emp);
    }
  }

  removePendingMember(empId: string): void {
    this.pendingMembers = this.pendingMembers.filter(m => m.id !== empId);
  }

  getMemberCurrentShift(employeeId: string): string {
    const assignment = this.assignments.find(a => a.employeeId === employeeId);
    return assignment ? assignment.shiftName : 'Not assigned';
  }

  getEmpDeptName(emp: any): string {
    if (emp.departmentId) {
      const dept = this.departments.find((d: any) => d.id === emp.departmentId);
      return dept ? dept.name : '—';
    }
    return '—';
  }

  private getDefaultShifts(): any[] {
    return [
      { id: 1, name: 'Morning Shift', startTime: '09:00', endTime: '18:00', graceMinutes: 15 },
      { id: 2, name: 'Evening Shift', startTime: '14:00', endTime: '23:00', graceMinutes: 10 },
      { id: 3, name: 'Night Shift', startTime: '22:00', endTime: '06:00', graceMinutes: 10 },
      { id: 4, name: 'General Shift', startTime: '10:00', endTime: '19:00', graceMinutes: 30 },
      { id: 5, name: 'Flexible', startTime: '', endTime: '', graceMinutes: 0 },
    ];
  }
}
